import React from 'react';
import { ColorPickerButton, ColorPickerSwatchInput } from '@ardoq/color-picker';
import type { CommonFieldProps, HexInputProps } from '@ardoq/forms';
import { FORMATTING_DEFAULT_PALETTE, FORMATTING_PALE_PALETTE } from '../consts';
import { ViewIds } from '@ardoq/api-types';
import { useFormattingContext } from './PerspectivesConditionalFormatting/useFormattingContext';
import { WithPopoverPlacement, WithPopover } from '@ardoq/popovers';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';

/** The default width of the color picker dropdown with 7 colors per row */
const DROPDOWN_WIDTH = 232;

type Props = {
  dataTestId: string;
  value: string;
  onValueChange: HexInputProps['onValueChange'];
  isNewConditionalFormatting: boolean;
  children?: React.ReactNode;
  variant?: 'swatch' | 'button';
  isDisabled?: CommonFieldProps['isDisabled'];
  hasWarning?: boolean;
  viewId?: string;
  colorPaletteAlignment?: 'left' | 'right';
};

export function FormattingColorPicker({
  value,
  isNewConditionalFormatting,
  onValueChange,
  dataTestId,
  children,
  isDisabled,
  variant = 'button',
  hasWarning,
  viewId: viewIdProp,
  colorPaletteAlignment = 'right',
}: Props) {
  const context = useFormattingContext();
  const viewId = viewIdProp ?? context.viewId;
  const ColorPickerComponent =
    variant === 'swatch' ? ColorPickerSwatchInput : ColorPickerButton;

  const palette =
    viewId === ViewIds.DEPENDENCY_MAP_2
      ? FORMATTING_PALE_PALETTE
      : FORMATTING_DEFAULT_PALETTE;

  return (
    <ColorPickerComponent
      dataTestId={dataTestId}
      value={value}
      alignDropdownTo={colorPaletteAlignment}
      onValueChange={onValueChange}
      isDisabled={isDisabled}
      hasWarning={hasWarning}
      {...(isNewConditionalFormatting
        ? {
            palettes: [palette],
            dropdownWidth: DROPDOWN_WIDTH,
            headerRightAddon: (
              <>
                <WithPopover
                  content="Default colors vary per view style to optimize for visual clarity. You can also select a custom color."
                  preferredPlacement={WithPopoverPlacement.TOP}
                >
                  <Icon
                    iconName={IconName.INFO}
                    iconSize={IconSize.MEDIUM}
                    color={colors.grey500}
                    style={{ marginBottom: '5px' }}
                  />
                </WithPopover>
              </>
            ),
          }
        : {})}
    >
      {children}
    </ColorPickerComponent>
  );
}
